import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import TicketList from './TicketList';
import TicketDetails from './TicketDetails';
import { SupportTicket } from '../../../types';
import { useTicket } from '../../../hooks/useApi';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbLabelContext';
import { LoadingSpinner } from '../../ui/Loading';

const TicketManagement: React.FC = () => {
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [searchParams] = useSearchParams();
  const { ticketId } = useParams();
  const navigate = useNavigate();
  
  // Prioritize path param (route-based), fallback to query param (legacy)
  const ticketParam = ticketId || searchParams.get('ticket');
  
  const { data: ticketResponse, isLoading, isError } = useTicket(ticketParam || '');
  useBreadcrumbLabel(ticketResponse?.data?.ticketNumber ?? selectedTicket?.ticketNumber);

  useEffect(() => {
    if (ticketParam) {
      // Clear current selection to avoid showing stale data while loading new ticket
      setSelectedTicket(null);
    }
  }, [ticketParam]);

  useEffect(() => {
    if (ticketParam) {
      if (ticketResponse?.data) {
        setSelectedTicket(ticketResponse.data);
      }
    } else {
      setSelectedTicket(null);
    }
  }, [ticketParam, ticketResponse]);

  const handleTicketSelect = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    navigate(`/tickets/${ticket.ticketNumber}`);
  };

  const handleBack = () => {
    setSelectedTicket(null);
    navigate('/tickets');
  };

  if (ticketParam && isLoading && !selectedTicket) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Handle invalid ticket
  if (ticketParam && isError && !isLoading) {
      return (
          <div className="container mx-auto p-6 text-center">
              <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-4">
                  Ticket not found or error loading ticket.
              </div>
              <button 
                onClick={handleBack}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                  Back to Ticket List
              </button>
          </div>
      )
  }

  return (
    <div className="container mx-auto p-6">
      {selectedTicket ? (
        <TicketDetails ticket={selectedTicket} onBack={handleBack} />
      ) : (
        <TicketList onTicketSelect={handleTicketSelect} />
      )}
    </div>
  );
};

export default TicketManagement;
